import { match } from 'ts-pattern';

import { Dialog, DialogContent } from '@nesso/ui/primitives/dialog';

import { EmailVerificationNotice } from '@/widgets/user/email-verification-notice';

import type { EmailChangeStep } from '../../../model/use-email-change';
import { ProfileEmailChangeConfirmIdentityStep } from './profile-email-change-dialog/profile-email-change-confirm-identity-step';
import { ProfileEmailChangeUpdateEmailStep } from './profile-email-change-dialog/profile-email-change-update-email-step';

type Props = {
    open: boolean;
    step: EmailChangeStep;
    isConfirmingIdentity: boolean;
    isSubmitting: boolean;
    identityError?: string;
    addressError?: string;
    onConfirmIdentity: (currentPassword: string) => void;
    onSubmitNewEmail: (newEmail: string) => void;
    onClose: () => void;
};

export function ProfileEmailChangeDialog({
    open,
    step,
    isConfirmingIdentity,
    isSubmitting,
    identityError,
    addressError,
    onConfirmIdentity,
    onSubmitNewEmail,
    onClose,
}: Props) {
    const isPending = isConfirmingIdentity || isSubmitting;

    return (
        <Dialog open={open} onOpenChange={(next) => !next && !isPending && onClose()}>
            <DialogContent showCloseButton={step.name !== 'verify' && !isPending}>
                {match(step)
                    .with({ name: 'confirm-identity' }, () => (
                        <ProfileEmailChangeConfirmIdentityStep
                            pending={isConfirmingIdentity}
                            error={identityError}
                            onContinue={onConfirmIdentity}
                            onCancel={onClose}
                        />
                    ))
                    .with({ name: 'update-email' }, () => (
                        <ProfileEmailChangeUpdateEmailStep
                            pending={isSubmitting}
                            fieldError={addressError}
                            onSubmit={onSubmitNewEmail}
                            onCancel={onClose}
                        />
                    ))
                    .with({ name: 'verify' }, ({ newEmail }) => (
                        <EmailVerificationNotice
                            title="Verify your new email address"
                            email={newEmail}
                            onClose={onClose}
                        />
                    ))
                    .exhaustive()}
            </DialogContent>
        </Dialog>
    );
}
